import { onScopeDispose, ref, shallowRef, type Ref, type ShallowRef } from 'vue'

import { chartValidationApi } from '../api/ChartValidationApi'
import type { ChartValidationRequest, ChartValidationResult } from '../models/chartValidation.types'
import { validateChartContract } from '../service/chartValidation'

export interface UseChartContractValidation {
  validating: Ref<boolean>
  result: ShallowRef<ChartValidationResult | null>
  error: ShallowRef<unknown | null>
  validate: (request: ChartValidationRequest) => Promise<ChartValidationResult | null>
  cancel: () => void
}

/** Abort-safe optional server validation; picker eligibility still comes from the catalog. */
export function useChartContractValidation(): UseChartContractValidation {
  const validating = ref(false)
  const result = shallowRef<ChartValidationResult | null>(null)
  const error = shallowRef<unknown | null>(null)
  let controller: AbortController | null = null
  let activeRequest = 0

  const cancel = (): void => {
    activeRequest += 1
    controller?.abort()
    controller = null
    validating.value = false
  }

  const validate = async (
    request: ChartValidationRequest,
  ): Promise<ChartValidationResult | null> => {
    controller?.abort()
    controller = new AbortController()
    const requestId = ++activeRequest
    validating.value = true
    error.value = null
    try {
      const next = await validateChartContract(request, chartValidationApi, controller.signal)
      if (requestId !== activeRequest || controller.signal.aborted) return null
      result.value = next
      return next
    } catch (cause) {
      if (requestId !== activeRequest || controller.signal.aborted) return null
      error.value = cause
      result.value = null
      return null
    } finally {
      if (requestId === activeRequest) {
        validating.value = false
        controller = null
      }
    }
  }

  onScopeDispose(cancel)
  return { validating, result, error, validate, cancel }
}
